// URL flags (IMPLEMENTATION §2.1): ?debug ?test ?seed ?fresh ?scenario ?render=none ?spike ?quality ?fps
// ?autosave=1 (M5: saving is off in ?test and ?scenario runs unless requested, §2.9 "Test mode")
// plus ?mipmaps=off (M0 spike negative control: Phaser without mipmapFilter) and, with ?test only,
// ?bootDelay=<ms> (simulates a slow renderer init so e2e can resize inside the boot window).
// M11, with ?test only: ?autosaveMs=<ms> (autosave period override for e2e write spies) and ?a2hs=1 (the
// one-time Add-to-Home-Screen card is off in ?test runs unless requested).
// M10, with ?test only: ?fx=1 runs the board / DOM FX (off in ?test: zero-duration tweens) for the FX e2e.
// With ?test only: ?howto=1 lets the building how-to cards show on unlock (off in ?test runs by default).
// PURE: main.ts passes location.search and import.meta.env values in.

export type RenderMode = 'phaser' | 'none';
export type Quality = 'high' | 'low';

export interface Env {
  debug: boolean;
  test: boolean;
  spike: boolean;
  fps: boolean;
  fresh: boolean;
  /** ?autosave=1: save even in ?test / ?scenario runs (off there by default). */
  autosave: boolean;
  render: RenderMode;
  quality: Quality;
  /** false with ?mipmaps=off: the renderer starts without `render.mipmapFilter` (spike control run). */
  mipmaps: boolean;
  /** ?test only: ms to wait before renderer init (0–5000; 0 = none). */
  bootDelayMs: number;
  /** ?test only: autosave period in ms (50–60000) instead of ui.json save.autosaveSec; null = the data. */
  autosaveMs: number | null;
  /** The one-time A2HS card may show (always outside ?test; in ?test only with ?a2hs=1). */
  a2hs: boolean;
  /** M10: ?test with ?fx=1 runs the FX (outside ?test they always run). */
  fx: boolean;
  /** The building how-to cards show on unlock (always outside ?test; in ?test only with ?howto=1). */
  howto: boolean;
  seed: number | null;
  scenario: string | null;
  dev: boolean;
  mode: string;
}

function flag(q: URLSearchParams, name: string): boolean {
  if (!q.has(name)) return false;
  const v = (q.get(name) ?? '').toLowerCase();
  return v !== '0' && v !== 'false' && v !== 'off';
}

function clampMs(raw: string | null): number {
  if (raw === null || !/^\d{1,5}$/.test(raw)) return 0;
  return Math.min(5000, Number.parseInt(raw, 10));
}

function periodMs(raw: string | null): number | null {
  if (raw === null || !/^\d{1,5}$/.test(raw)) return null;
  return Math.min(60_000, Math.max(50, Number.parseInt(raw, 10)));
}

export function parseEnv(search: string, dev: boolean, mode: string): Env {
  const q = new URLSearchParams(search);
  const seedRaw = q.get('seed');
  const seedNum = seedRaw !== null && /^-?\d+$/.test(seedRaw) ? Number.parseInt(seedRaw, 10) : null;
  // Only safe integers reach the sim (emptyState throws on anything else); out-of-range seeds are ignored.
  const seed = seedNum !== null && Number.isSafeInteger(seedNum) ? seedNum : null;
  const scenario = q.get('scenario');
  return {
    debug: flag(q, 'debug'),
    test: flag(q, 'test'),
    spike: flag(q, 'spike'),
    fps: flag(q, 'fps'),
    fresh: flag(q, 'fresh'),
    autosave: flag(q, 'autosave'),
    render: q.get('render') === 'none' ? 'none' : 'phaser',
    quality: q.get('quality') === 'low' ? 'low' : 'high',
    mipmaps: q.get('mipmaps') !== 'off',
    bootDelayMs: flag(q, 'test') ? clampMs(q.get('bootDelay')) : 0,
    autosaveMs: flag(q, 'test') ? periodMs(q.get('autosaveMs')) : null,
    a2hs: !flag(q, 'test') || flag(q, 'a2hs'),
    fx: !flag(q, 'test') || flag(q, 'fx'),
    howto: !flag(q, 'test') || flag(q, 'howto'),
    seed,
    scenario: scenario !== null && /^[A-Za-z0-9_-]{1,64}$/.test(scenario) ? scenario : null,
    dev,
    mode,
  };
}

/** `window.__gm` is exposed with ?test, ?debug, in DEV, or in the `--mode test` build (§2.9). */
export function hooksEnabled(env: Env): boolean {
  return env.test || env.debug || env.dev || env.mode === 'test';
}
